"use client"

import * as React from "react"
import { Alert, AlertDescription, AlertTitle } from '../../components/alert'
import { UIButton } from '../buttons/ui-button'
import { UIBox } from '../layout/ui-box'
import { AlertTriangle, CheckCircle, Info, AlertCircle, X } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface UIAlertProps extends React.ComponentProps<typeof Alert> {}
export interface UIAlertTitleProps extends React.ComponentProps<typeof AlertTitle> {}
export interface UIAlertDescriptionProps extends React.ComponentProps<typeof AlertDescription> {}

export interface UIAlertCalloutProps extends React.ComponentProps<typeof Alert> {}

export interface UIAlertWithActionsProps extends React.ComponentProps<typeof Alert> {
  actions?: React.ReactNode
}

export interface UIAlertDismissibleProps extends Omit<React.ComponentProps<typeof Alert>, 'children'> {
  onDismiss: () => void
  dismissible?: boolean
  icon?: React.ReactNode
  children: React.ReactNode
}

/**
 * Alert Component (Base)
 * Direct wrapper of shadcn/ui Alert with UI* naming and glassmorphism
 */
export const UIAlert = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  UIAlertProps
>(({ className, children, ...props }, ref) => (
  <Alert
    ref={ref}
    className={cn(
      // Background with refined transparency
      "bg-background/80 dark:bg-gray-900/50",
      // Clean, visible borders
      "border border-gray-200 dark:border-gray-800",
      // Subtle inner glow for glass effect
      "ring-1 ring-white/10 dark:ring-white/5",
      // Refined shadow system
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      // Smooth transitions
      "transition-colors duration-300 ease-out",
      className
    )}
    {...props}
  >
    {children}
  </Alert>
))

export const UIAlertTitle = React.forwardRef<
  React.ComponentRef<typeof AlertTitle>,
  UIAlertTitleProps
>(({ ...props }, ref) => (
  <AlertTitle ref={ref} {...props} />
))

export const UIAlertDescription = React.forwardRef<
  React.ComponentRef<typeof AlertDescription>,
  UIAlertDescriptionProps
>(({ ...props }, ref) => (
  <AlertDescription ref={ref} {...props} />
))

/**
 * Destructive Alert
 * Based on shadcn/ui "Destructive Alert" block
 */
export const UIAlertDestructive = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  React.ComponentProps<typeof Alert>
>(({ className, children, ...props }, ref) => (
  <Alert
    ref={ref}
    variant="destructive"
    className={cn(
      "bg-background/80 dark:bg-gray-900/50",
      "ring-1 ring-white/10 dark:ring-white/5",
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      "transition-colors duration-300 ease-out",
      className
    )}
    {...props}
  >
    <AlertTriangle className="size-4" />
    {children}
  </Alert>
))

/**
 * Success Alert
 * Based on shadcn/ui "Success Alert" block
 */
export const UIAlertSuccess = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  React.ComponentProps<typeof Alert>
>(({ className, children, ...props }, ref) => (
  <Alert
    ref={ref}
    className={cn(
      "bg-background/80 dark:bg-gray-900/50",
      "ring-1 ring-white/10 dark:ring-white/5",
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      "transition-colors duration-300 ease-out",
      // Success styling
      "border-emerald-600/50 dark:border-emerald-600/40",
      "text-emerald-700 dark:text-emerald-400",
      "bg-emerald-50/50 dark:bg-emerald-950/30",
      "[&>svg]:text-emerald-600 dark:[&>svg]:text-emerald-400",
      className
    )}
    {...props}
  >
    <CheckCircle className="size-4" />
    {children}
  </Alert>
))

/**
 * Warning Alert
 * Based on shadcn/ui pattern with warning styling
 */
export const UIAlertWarning = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  React.ComponentProps<typeof Alert> & { actions?: React.ReactNode }
>(({ className, children, actions, ...props }, ref) => (
  <Alert
    ref={ref}
    className={cn(
      "bg-background/80 dark:bg-gray-900/50",
      "ring-1 ring-white/10 dark:ring-white/5",
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      "transition-colors duration-300 ease-out",
      // Warning styling
      "border-amber-600/50 dark:border-amber-600/40",
      "text-amber-700 dark:text-amber-400",
      "bg-amber-50/50 dark:bg-amber-950/30",
      "[&>svg]:text-amber-600 dark:[&>svg]:text-amber-400",
      actions && "flex items-center [&>svg]:shrink-0",
      className
    )}
    {...props}
  >
    <AlertTriangle className="size-4" />
    {actions ? (
      <>
        <UIBox className="flex-1">{children}</UIBox>
        {actions}
      </>
    ) : (
      children
    )}
  </Alert>
))

/**
 * Info Alert
 * Based on shadcn/ui pattern with info styling
 */
export const UIAlertInfo = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  React.ComponentProps<typeof Alert>
>(({ className, children, ...props }, ref) => (
  <Alert
    ref={ref}
    className={cn(
      "bg-background/80 dark:bg-gray-900/50",
      "ring-1 ring-white/10 dark:ring-white/5",
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      "transition-colors duration-300 ease-out",
      // Info styling
      "border-blue-600/50 dark:border-blue-600/40",
      "text-blue-700 dark:text-blue-400",
      "bg-blue-50/50 dark:bg-blue-950/30",
      "[&>svg]:text-blue-600 dark:[&>svg]:text-blue-400",
      className
    )}
    {...props}
  >
    <Info className="size-4" />
    {children}
  </Alert>
))

/**
 * Callout Alert
 * Based on shadcn/ui "Callout" block - subtle styling for information
 */
export const UIAlertCallout = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  UIAlertCalloutProps
>(({ className, children, ...props }, ref) => (
  <Alert
    ref={ref}
    className={cn(
      "bg-background/80 dark:bg-gray-900/50",
      "ring-1 ring-white/10 dark:ring-white/5",
      "shadow-sm shadow-gray-950/5 dark:shadow-black/20",
      "transition-colors duration-300 ease-out",
      // Callout styling
      "border-gray-300 dark:border-gray-700",
      "bg-muted/50 dark:bg-muted/30",
      "text-muted-foreground",
      "[&>svg]:text-muted-foreground",
      className
    )}
    {...props}
  >
    <Info className="size-4" />
    {children}
  </Alert>
))

/**
 * Alert with Actions
 * Based on shadcn/ui "Alert with Actions" block - includes action buttons
 */
export const UIAlertWithActions = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  UIAlertWithActionsProps
>(({ className, children, actions, ...props }, ref) => (
  <Alert
    ref={ref}
    className={className}
    {...props}
  >
    <AlertCircle className="size-4" />
    <UIBox className="flex-1">
      {children}
      {actions && (
        <UIBox className="mt-3 flex gap-2">
          {actions}
        </UIBox>
      )}
    </UIBox>
  </Alert>
))

UIAlert.displayName = "UIAlert"
UIAlertTitle.displayName = "UIAlertTitle"
UIAlertDescription.displayName = "UIAlertDescription"
UIAlertDestructive.displayName = "UIAlertDestructive"
UIAlertSuccess.displayName = "UIAlertSuccess"
UIAlertWarning.displayName = "UIAlertWarning"
UIAlertInfo.displayName = "UIAlertInfo"
/**
 * Dismissible Alert
 * Based on shadcn/ui "Alert with Actions" block - can be dismissed with X button
 */
export const UIAlertDismissible = React.forwardRef<
  React.ComponentRef<typeof Alert>,
  UIAlertDismissibleProps
>(({ className, children, onDismiss, dismissible = true, icon, ...props }, ref) => {
  const IconComponent = icon || <AlertCircle className="size-4 flex-shrink-0" />

  return (
    <Alert
      ref={ref}
      className={`flex justify-between items-center pr-2 [&>svg+div]:translate-y-0 ${className || ''}`}
      {...props}
    >
      <UIBox className="flex items-start gap-3">
        {IconComponent}
        <UIBox className="flex-col justify-center">
          {children}
        </UIBox>
      </UIBox>
      {dismissible && (
        <UIButton
          size="icon"
          variant="ghost"
          className="h-6 w-6 shrink-0"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </UIButton>
      )}
    </Alert>
  )
})

UIAlertCallout.displayName = "UIAlertCallout"
UIAlertWithActions.displayName = "UIAlertWithActions"
UIAlertDismissible.displayName = "UIAlertDismissible"